import { formatPaise, GST_STATES } from '@oneapp/gst';
import type { ImportCheck, ImportCheckInvoice } from '@oneapp/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDate } from '../business/parts';
import { Icon } from '../components/icons';
import { Sheet } from '../components/Sheet';
import { Button, Callout } from '../components/ui';
import { ApiError } from '../lib/api';
import { importKeys, importsApi, parseChecksPath } from './api';
import { PhotoView } from './PhotoView';

/**
 * U3 · Photos: check only what I wasn't sure of. One invoice at a time: the photo with the unclear
 * spot marked (tap it to look closer), what was read for sure with a tick, and each field to check,
 * filled with what was read, to confirm or correct against the bill. Opened from the chat's "Check now"
 * (a sheet over it) or on its own from a link; it closes when the last one is confirmed.
 */

export type ChecksFinish = 'saved' | 'back';

interface Screen {
  label: string;
  aside: ReactNode;
  body: ReactNode;
  footer: ReactNode;
}

/** "3", "3 and 4", "3, 4 and 5". */
function listOf(numbers: number[], and: string): string {
  if (numbers.length < 2) return numbers.join('');
  return `${numbers.slice(0, -1).join(', ')} ${and} ${numbers[numbers.length - 1]}`;
}

function useChecksScreen(
  importId: string,
  finish: (how: ChecksFinish) => void,
  /** In the chat's sheet: closing tells the chat, so the server needn't. */
  inChat: boolean,
): Screen {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const formId = useId();
  const query = useQuery({
    queryKey: importKeys.checks(importId),
    queryFn: ({ signal }) => importsApi.checks(importId, signal),
    staleTime: 0,
  });
  const [values, setValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, { message: string; suggestion?: string }>>({});
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const invoices = query.data?.invoices ?? [];
  const current = invoices.find((i) => i.checks.some((c) => c.status === 'open'));
  const open = current?.checks.filter((c) => c.status === 'open') ?? [];
  const doneCount = invoices.filter((i) => i.checks.every((c) => c.status !== 'open')).length;

  // Every check answered (here, or on another device): nothing left to show, and the chat carries on.
  const finished = query.isSuccess && invoices.length > 0 && !current;
  const told = useRef(false);
  useEffect(() => {
    if (finished && !told.current) {
      told.current = true;
      finish('saved');
    }
  }, [finished, finish]);

  const screen: Screen = { label: t('imports.checks.label'), aside: null, body: null, footer: null };
  if (query.isPending) {
    screen.body = <p className="muted">{t('sales.loading')}</p>;
    return screen;
  }
  if (!query.data || !current) {
    const notFound = query.error instanceof ApiError && query.error.status === 404;
    screen.body =
      query.data && !current ? (
        <Callout tone="green" icon="check-circle">
          {t('imports.checks.done')}
        </Callout>
      ) : (
        <Callout tone="error" icon="alert-triangle">
          {notFound ? t('imports.checks.notFound') : t('sales.loadFailed')}
        </Callout>
      );
    return screen;
  }

  const answerOf = (check: ImportCheck) => values[check.id] ?? check.value ?? '';
  const confirm = async () => {
    setBusy(true);
    setFailed(false);
    const found: Record<string, { message: string; suggestion?: string }> = {};
    try {
      for (const check of open) {
        try {
          await importsApi.confirm(importId, check.id, answerOf(check), inChat ? { tellChat: false } : {});
        } catch (error) {
          if (!(error instanceof ApiError)) throw error;
          // Checked already (another device): nothing to do. Anything else wrong with the value: say so.
          if (error.problem?.code === 'PROBLEM_CLOSED') continue;
          if (error.status !== 422) throw error;
          const suggestion = error.problem?.suggestion;
          found[check.id] = {
            message: error.problem?.detail ?? t('chat.widget.invalid'),
            ...(suggestion ? { suggestion } : {}),
          };
        }
      }
      setErrors(found);
      if (Object.keys(found).length) {
        // The refused field whole, with what's wrong and the value that would do, under the footer's edge.
        requestAnimationFrame(() =>
          document
            .getElementById(formId)
            ?.querySelector('[aria-invalid="true"]')
            ?.closest('.check__field')
            ?.scrollIntoView?.({ block: 'nearest' }),
        );
      } else {
        setValues({});
        const fresh = await importsApi.checks(importId);
        queryClient.setQueryData(importKeys.checks(importId), fresh);
        void queryClient.invalidateQueries({ queryKey: importKeys.import(importId) });
      }
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  const total = invoices.length;
  const last = doneCount + 1 === total;
  screen.aside = <span className="count-badge">{t('imports.checks.of', { n: doneCount + 1, count: total })}</span>;
  screen.body = (
    <form
      id={formId}
      className="check"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        void confirm();
      }}
    >
      <InvoiceHead invoice={current} open={open} />
      {failed ? (
        <Callout tone="error" icon="alert-triangle" role="alert">
          {t('imports.checks.failed')}
        </Callout>
      ) : null}
      {current.sure.length ? (
        <ul className="check__sure" aria-label={t('imports.checks.sure')}>
          {current.sure.map((item) => (
            <li key={`${item.label}-${item.value}`}>
              <Icon name="check-circle" size={18} />
              <span className="check__sure-label">{item.label}</span>
              <span className="check__sure-value">{item.value}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {open.map((check) => (
        <CheckField
          key={check.id}
          check={check}
          value={answerOf(check)}
          error={errors[check.id]}
          onChange={(value) => {
            setValues((v) => ({ ...v, [check.id]: value }));
            // What was refused was the old value: an edit starts again.
            setErrors((e) => {
              if (!e[check.id]) return e;
              const rest = { ...e };
              delete rest[check.id];
              return rest;
            });
          }}
        />
      ))}
    </form>
  );
  screen.footer = (
    <div className="sheet__buttons sheet__buttons--stack">
      <Button type="submit" form={formId} busy={busy}>
        {last ? t('imports.checks.confirmLast') : t('imports.checks.confirm')}
      </Button>
      <p className="sheet__foot-note">{t('imports.checks.footnote')}</p>
    </div>
  );
  return screen;
}

/** The invoice's name, where it's from, and its photo with what needs a look. */
function InvoiceHead({ invoice, open }: { invoice: ImportCheckInvoice; open: ImportCheck[] }) {
  const { t } = useTranslation();
  const first = open[0];
  const title = [invoice.invoiceNumber, invoice.buyerName].filter(Boolean).join(' · ');
  const photo = invoice.photos.find((p) => p.uploadId === first?.area?.uploadId) ?? invoice.photos[0];
  const labels = open.map((c) => c.label);
  return (
    <div className="check__head">
      <div className="check__titles">
        <h2 className="sheet__title">{title || t('imports.checks.invoice', { invoice: invoice.invoice })}</h2>
        <p className="check__sub">
          {t('imports.checks.sub', {
            invoice: invoice.invoice,
            count: invoice.invoices,
            photos: listOf(
              invoice.photos.map((p) => p.number),
              t('imports.checks.and'),
            ),
          })}
        </p>
      </div>
      <div className="check__photo-row">
        {photo ? (
          <PhotoView
            uploadId={photo.uploadId}
            number={photo.number}
            area={first?.area && first.area.uploadId === photo.uploadId ? first.area : null}
            tilt
          />
        ) : null}
        {first ? (
          <div className="check__why">
            <span className="check__why-title">
              <Icon name="alert-triangle" size={15} />
              {t(`imports.checks.reason.${first.reason}`)}
            </span>
            <span className="check__why-text">
              {labels.length === 1
                ? t('imports.checks.whyOne', { label: labels[0] })
                : t('imports.checks.whyMany', { labels: labels.join(', ') })}
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

const RATES = ['0', '0.25', '3', '5', '12', '18', '28', '40'];

function CheckField({
  check,
  value,
  error,
  onChange,
}: {
  check: ImportCheck;
  value: string;
  /** Why the value was refused, and a value that would do. */
  error: { message: string; suggestion?: string } | undefined;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const helpId = `${id}-help`;
  const date = useDate();
  const suggestion = error?.suggestion ?? check.suggestion;
  // The value as people read it: ₹45,000, 18%, 9 Aug 2026, Karnataka.
  const shown = (value: string) => {
    switch (check.field) {
      case 'taxableValue':
      case 'invoiceValue':
        return Number.isFinite(Number(value)) ? formatPaise(Math.round(Number(value) * 100)) : value;
      case 'rate':
        return `${value}%`;
      case 'invoiceDate':
        return /^\d{4}-\d{2}-\d{2}$/.test(value) ? date(value) : value;
      case 'placeOfSupply':
        return GST_STATES[value] ?? value;
      default:
        return value;
    }
  };
  const common = {
    id,
    value,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': helpId,
    className: `input check__input${check.field === 'buyerGstin' ? ' input--mono' : ''}`,
  } as const;
  let control: ReactNode;
  switch (check.field) {
    case 'invoiceDate':
      control = <input {...common} type="date" onChange={(e) => onChange(e.target.value)} />;
      break;
    case 'placeOfSupply':
      control = (
        <select {...common} className={`${common.className} select`} onChange={(e) => onChange(e.target.value)}>
          <option value="" disabled>
            {t('imports.checks.chooseState')}
          </option>
          {Object.entries(GST_STATES).map(([code, name]) => (
            <option key={code} value={code}>
              {name}
            </option>
          ))}
        </select>
      );
      break;
    case 'rate':
      control = (
        <select {...common} className={`${common.className} select`} onChange={(e) => onChange(e.target.value)}>
          {(RATES.includes(value) || !value ? RATES : [value, ...RATES]).map((rate) => (
            <option key={rate} value={rate}>
              {rate}%
            </option>
          ))}
        </select>
      );
      break;
    case 'taxableValue':
    case 'invoiceValue':
      control = (
        <div className="money">
          <input {...common} inputMode="decimal" onChange={(e) => onChange(e.target.value)} />
        </div>
      );
      break;
    default:
      control = (
        <input
          {...common}
          inputMode={check.field === 'hsn' || check.field === 'quantity' ? 'decimal' : undefined}
          maxLength={check.field === 'buyerGstin' ? 15 : 40}
          autoCapitalize={check.field === 'buyerGstin' || check.field === 'invoiceNumber' ? 'characters' : undefined}
          autoComplete="off"
          spellCheck={false}
          onChange={(e) =>
            onChange(check.field === 'buyerGstin' ? e.target.value.toUpperCase().replace(/\s/g, '') : e.target.value)
          }
        />
      );
  }
  return (
    <div className="field check__field">
      <label className="field__label" htmlFor={id}>
        {t('imports.checks.pleaseCheck', { label: check.label })}
      </label>
      {control}
      <p id={helpId} className={`field__help${error ? ' field__help--error' : ' check__hint'}`}>
        {error?.message ?? check.hint}
      </p>
      {suggestion && suggestion !== value ? (
        <button type="button" className="chip check__use" onClick={() => onChange(suggestion)}>
          {t('imports.checks.use', { value: shown(suggestion) })}
        </button>
      ) : null}
    </div>
  );
}

/** Opened from the chat's "Check now": the sheet over it. */
export function CheckSheet({
  path,
  onClose,
  onFinish,
}: {
  path: string;
  onClose: () => void;
  onFinish: (how: ChecksFinish) => void;
}) {
  const parsed = parseChecksPath(path);
  const screen = useChecksScreen(parsed?.importId ?? '', onFinish, true);
  return (
    <Sheet open onClose={onClose} label={screen.label} aside={screen.aside} footer={screen.footer}>
      {screen.body}
    </Sheet>
  );
}

/** The same page on its own, for a link to it. */
export function ChecksStandalone({
  importId,
  onFinish,
  frame,
}: {
  importId: string;
  onFinish: (how: ChecksFinish) => void;
  frame: (screen: Screen) => ReactNode;
}) {
  const screen = useChecksScreen(importId, onFinish, false);
  return <>{frame(screen)}</>;
}
